import type { ApprovedProductionCalendar } from '@/shared/production-calendar'

export const MAX_WORKING_DAYS = 10_000

export type Direction = 'add' | 'subtract'
export type DateUnit = 'days' | 'weeks' | 'months' | 'years'
export type WorkingCalendarBasis = 'weekends_only' | 'ru_federal_five_day'

export interface CalendarDateResult {
  kind: 'success'
  baseDate: string
  resultDate: string
  direction: Direction
  unit: DateUnit
  amount: number
  clampedMonthEnd: boolean
}

export interface WorkingDateResult {
  kind: 'success'
  baseDate: string
  resultDate: string
  direction: Direction
  amount: number
  basis: WorkingCalendarBasis
  skippedOrdinaryWeekends: number
  skippedFederalHolidaysOrTransfers: number
  coveredCalendars: readonly ApprovedProductionCalendar[]
}

export interface WorkingDateInputError {
  kind: 'invalid-input'
  reason: 'invalid_date' | 'invalid_amount'
}

export interface WorkingDateUnsupportedError {
  kind: 'unsupported-input'
  reason: 'date_range' | 'coverage_gap'
  missingYears: readonly number[]
}

export interface WorkingDateProcessingError {
  kind: 'processing-error'
  reason: 'incomplete_calendar'
}

export type WorkingDateCalculation =
  | WorkingDateResult
  | WorkingDateInputError
  | WorkingDateUnsupportedError
  | WorkingDateProcessingError

export function calculateCalendarDate(
  baseDate: string,
  direction: Direction,
  amount: number,
  unit: DateUnit,
): CalendarDateResult | null {
  const parts = parseIsoDate(baseDate)
  if (!parts || !Number.isInteger(amount) || amount < 0) return null

  const signedAmount = direction === 'add' ? amount : -amount
  if (unit === 'days' || unit === 'weeks') {
    const resultDate = shiftIsoDate(baseDate, signedAmount * (unit === 'weeks' ? 7 : 1))
    return resultDate
      ? { kind: 'success', baseDate, resultDate, direction, unit, amount, clampedMonthEnd: false }
      : null
  }

  const monthAmount = signedAmount * (unit === 'years' ? 12 : 1)
  const targetMonthIndex = parts.year * 12 + parts.month - 1 + monthAmount
  const targetYear = Math.floor(targetMonthIndex / 12)
  const targetMonth = targetMonthIndex - targetYear * 12 + 1
  if (targetYear < 1 || targetYear > 9999) return null

  const targetDay = Math.min(parts.day, daysInMonth(targetYear, targetMonth))
  return {
    kind: 'success',
    baseDate,
    resultDate: formatIsoDate(targetYear, targetMonth, targetDay),
    direction,
    unit,
    amount,
    clampedMonthEnd: targetDay !== parts.day,
  }
}

export function calculateWorkingDate(
  baseDate: string,
  direction: Direction,
  amount: number,
  basis: WorkingCalendarBasis,
  calendars: readonly ApprovedProductionCalendar[],
): WorkingDateCalculation {
  if (!parseIsoDate(baseDate)) return { kind: 'invalid-input', reason: 'invalid_date' }
  if (!Number.isInteger(amount) || amount < 0 || amount > MAX_WORKING_DAYS) {
    return { kind: 'invalid-input', reason: 'invalid_amount' }
  }

  const calendarsByYear = new Map<number, ApprovedProductionCalendar>(
    calendars.map((calendar) => [calendar.year, calendar]),
  )
  const daysByYear = new Map<number, Map<string, ApprovedProductionCalendar['days'][number]>>(
    calendars.map((calendar) => [
      calendar.year,
      new Map(calendar.days.map((day) => [day.date, day])),
    ]),
  )
  const touchedYears = new Set<number>([Number(baseDate.slice(0, 4))])
  if (amount === 0) {
    const baseCalendar = calendarsByYear.get(Number(baseDate.slice(0, 4)))
    return {
      kind: 'success',
      baseDate,
      resultDate: baseDate,
      direction,
      amount,
      basis,
      skippedOrdinaryWeekends: 0,
      skippedFederalHolidaysOrTransfers: 0,
      coveredCalendars: basis === 'ru_federal_five_day' && baseCalendar
        ? [baseCalendar]
        : [],
    }
  }

  const initialCoverageError = coverageError(basis, touchedYears, calendarsByYear)
  if (initialCoverageError) return initialCoverageError

  let currentDate = baseDate
  let countedWorkingDays = 0
  let skippedOrdinaryWeekends = 0
  let skippedFederalHolidaysOrTransfers = 0
  const step = direction === 'add' ? 1 : -1

  while (countedWorkingDays < amount) {
    const nextDate = shiftIsoDate(currentDate, step)
    if (!nextDate) {
      return { kind: 'unsupported-input', reason: 'date_range', missingYears: [] }
    }
    currentDate = nextDate
    const year = Number(currentDate.slice(0, 4))
    touchedYears.add(year)

    const gap = coverageError(basis, touchedYears, calendarsByYear)
    if (gap) return gap

    if (basis === 'weekends_only') {
      if (isIsoWeekend(currentDate)) skippedOrdinaryWeekends += 1
      else countedWorkingDays += 1
      continue
    }

    const day = daysByYear.get(year)?.get(currentDate)
    if (!day) return { kind: 'processing-error', reason: 'incomplete_calendar' }
    if (day.status === 'working') countedWorkingDays += 1
    else if (day.sourceRefs.length === 0) skippedOrdinaryWeekends += 1
    else skippedFederalHolidaysOrTransfers += 1
  }

  return {
    kind: 'success',
    baseDate,
    resultDate: currentDate,
    direction,
    amount,
    basis,
    skippedOrdinaryWeekends,
    skippedFederalHolidaysOrTransfers,
    coveredCalendars: basis === 'ru_federal_five_day'
      ? [...touchedYears]
        .sort((left, right) => left - right)
        .map((year) => calendarsByYear.get(year) as ApprovedProductionCalendar)
      : [],
  }
}

function coverageError(
  basis: WorkingCalendarBasis,
  years: ReadonlySet<number>,
  calendarsByYear: ReadonlyMap<number, ApprovedProductionCalendar>,
): WorkingDateUnsupportedError | null {
  if (basis !== 'ru_federal_five_day') return null
  const missingYears = [...years]
    .filter((year) => !calendarsByYear.has(year))
    .sort((left, right) => left - right)
  return missingYears.length > 0
    ? { kind: 'unsupported-input', reason: 'coverage_gap', missingYears }
    : null
}

function shiftIsoDate(value: string, amount: number): string | null {
  const date = new Date(`${value}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + amount)
  if (Number.isNaN(date.getTime())) return null
  const result = date.toISOString().slice(0, 10)
  return parseIsoDate(result) ? result : null
}

function isIsoWeekend(value: string): boolean {
  const weekday = new Date(`${value}T00:00:00Z`).getUTCDay()
  return weekday === 0 || weekday === 6
}

function parseIsoDate(value: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null
  }
  return { year, month, day }
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
    return leap ? 29 : 28
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

function formatIsoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}
